import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';
import { supabase } from 'lib/supabase';
import { createDonorFeedback } from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import { formatMoney } from 'helpers/utils';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

// Helper to get current session token
const getAccessToken = async () => {
  const {
    data: { session }
  } = await supabase.auth.getSession();
  return session?.access_token;
};

// Helper function to call Edge Functions
const callEdgeFunction = async (functionName, data, token) => {
  const accessToken = token || (await getAccessToken());
  if (!accessToken) {
    throw new Error('No authentication token available');
  }
  console.log(`Calling edge function: ${functionName}`, data);
  const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  const result = await response.json();
  console.log(`Edge function response: ${functionName}`, result);

  if (result.error || !response.ok) {
    console.error(`Edge function error: ${functionName}`, result);
    throw new Error(result.error || result.message || 'Request failed');
  }

  return result.data;
};

// Hook to create payment intent
export function useCreatePaymentIntent() {
  const mutation = useMutation({
    mutationFn: ({
      donorId,
      amount,
      orderDetails,
      subtotal,
      transactionFees,
      giftAidEligible,
      giftAidData,
      isRecurring
    }) => {
      return callEdgeFunction('create-payment-intent', {
        donorId,
        amount,
        orderDetails,
        subtotal,
        transactionFees,
        giftAidEligible,
        giftAidData,
        isRecurring
      });
    },
    onError: error => {
      console.error('Create payment intent error:', error);
      toast.error('Failed to create payment intent');
    }
  });

  return {
    createPaymentIntent: mutation.mutate,
    createPaymentIntentAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to charge existing customer
export function useChargeExistingCustomer() {
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: ({ donorId, amount, description, paymentMethodId }) => {
      return callEdgeFunction('charge-existing-customer', {
        donorId,
        amount,
        description,
        paymentMethodId,
        branch_id: branchId
      });
    },
    onSuccess: () => {
      toast.success('Payment successful');
    },
    onError: error => {
      console.error('Charge customer error:', error);
      toast.error('Failed to process payment');
    }
  });

  return {
    chargeCustomer: mutation.mutate,
    chargeCustomerAsync: mutation.mutateAsync,
    isCharging: mutation.isPending,
    error: mutation.error
  };
}

// Hook to charge an auto pledge immediately (staff path of the
// process-pledge-payments function; the daily cron uses the same function)
export function useProcessPledgePayment() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ pledgeId }) => {
      return callEdgeFunction('process-pledge-payments', { pledgeId });
    },
    onSuccess: data => {
      const result = data?.results?.[0];
      if (result?.ok) {
        toast.success('Pledge payment charged');
      } else {
        toast.error(`Charge failed: ${result?.error || 'unknown error'}`);
      }
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
      queryClient.invalidateQueries({ queryKey: ['pledge'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: error => {
      console.error('Process pledge payment error:', error);
      toast.error('Failed to charge pledge payment');
    }
  });

  return {
    processPledgePayment: mutation.mutate,
    processPledgePaymentAsync: mutation.mutateAsync,
    isProcessingPledge: mutation.isPending
  };
}

// Hook to create Stripe customer
export function useCreateStripeCustomer() {
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: ({ donorId, organisationId }) => {
      return callEdgeFunction('create-stripe-customer', {
        donorId,
        organisationId,
        branch_id: branchId
      });
    },
    onSuccess: () => {
      toast.success('Stripe account created successfully');
    },
    onError: error => {
      console.error('Create Stripe customer error:', error);
      toast.error('Failed to create Stripe account');
    }
  });

  return {
    createStripeCustomer: mutation.mutate,
    createStripeCustomerAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to attach payment method
export function useAttachPaymentMethod() {
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: ({ donorId, paymentMethodId, setAsDefault = true }) => {
      return callEdgeFunction('attach-payment-method', {
        donorId,
        paymentMethodId,
        setAsDefault,
        branch_id: branchId
      });
    },
    onSuccess: () => {
      toast.success('Payment method attached successfully');
    },
    onError: error => {
      console.error('Attach payment method error:', error);
      toast.error('Failed to attach payment method');
    }
  });

  return {
    attachPaymentMethod: mutation.mutate,
    attachPaymentMethodAsync: mutation.mutateAsync,
    isAttaching: mutation.isPending,
    error: mutation.error
  };
}

// Hook to manage payment methods
export function useManagePaymentMethods() {
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: ({ action, donorId, paymentMethodId }) => {
      return callEdgeFunction('manage-payment-methods', {
        action,
        donorId,
        paymentMethodId,
        branch_id: branchId
      });
    },
    onSuccess: (data, variables) => {
      if (variables.action === 'delete') {
        toast.success('Payment method deleted successfully');
      } else if (variables.action === 'setDefault') {
        toast.success('Default payment method updated');
      }
    },
    onError: error => {
      console.error('Manage payment methods error:', error);
      toast.error('Failed to manage payment method');
    }
  });

  return {
    managePaymentMethods: mutation.mutate,
    managePaymentMethodsAsync: mutation.mutateAsync,
    isManaging: mutation.isPending,
    error: mutation.error
  };
}

// Hook to create gift aid
export function useCreateGiftAid() {
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: ({ orderId }) => {
      return callEdgeFunction('create-gift-aid', {
        orderId,
        branch_id: branchId
      });
    },
    onSuccess: data => {
      if (data.message === 'Gift Aid added successfully') {
        toast.success('Gift Aid added successfully');
      }
    },
    onError: error => {
      console.error('Create gift aid error:', error);
      toast.error('Failed to create Gift Aid');
    }
  });

  return {
    createGiftAid: mutation.mutate,
    createGiftAidAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Helper hooks for common operations

// Get all payment methods for a donor
export function useGetPaymentMethods() {
  const { managePaymentMethodsAsync } = useManagePaymentMethods();

  const getPaymentMethods = donorId => {
    return managePaymentMethodsAsync({ action: 'list', donorId });
  };

  return { getPaymentMethods };
}

// Get default payment method
export function useGetDefaultPaymentMethod() {
  const { managePaymentMethodsAsync } = useManagePaymentMethods();

  const getDefaultPaymentMethod = donorId => {
    return managePaymentMethodsAsync({ action: 'getDefault', donorId });
  };

  return { getDefaultPaymentMethod };
}

// Set default payment method
export function useSetDefaultPaymentMethod() {
  const { managePaymentMethodsAsync } = useManagePaymentMethods();

  const setDefaultPaymentMethod = (donorId, paymentMethodId) => {
    return managePaymentMethodsAsync({
      action: 'setDefault',
      donorId,
      paymentMethodId
    });
  };

  return { setDefaultPaymentMethod };
}

// Delete payment method
export function useDeletePaymentMethod() {
  const { managePaymentMethodsAsync } = useManagePaymentMethods();

  const deletePaymentMethod = (donorId, paymentMethodId) => {
    return managePaymentMethodsAsync({
      action: 'delete',
      donorId,
      paymentMethodId
    });
  };

  return { deletePaymentMethod };
}

// Hook to create order with payment
export function useCreateOrder() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: orderData => {
      return callEdgeFunction('create-order', orderData);
    },
    onSuccess: () => {
      toast.success('Order created successfully');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['donorOrders'] });
      queryClient.invalidateQueries({ queryKey: ['pledge'] });
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
      queryClient.invalidateQueries({ queryKey: ['donationAmount'] });
      queryClient.invalidateQueries({ queryKey: ['recentOrders'] });
    },
    onError: error => {
      console.error('Create order error:', error);
      toast.error('Failed to create order');
    }
  });

  return {
    createOrder: mutation.mutate,
    createOrderAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
    data: mutation.data
  };
}

// Hook to cancel a subscription
export function useCancelSubscription() {
  const { branchId } = useBranchPermission();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ subscriptionId, reason, notes }) => {
      return callEdgeFunction('cancel-subscription', {
        subscriptionId,
        branchId,
        reason,
        notes
      });
    },
    onSuccess: async (_data, variables) => {
      toast.success('Subscription cancelled');
      queryClient.invalidateQueries({ queryKey: ['recurring-donations'] });
      queryClient.invalidateQueries({ queryKey: ['donor-subscriptions'] });

      // Mirror the cancellation into the donor's Notes tab so the history
      // is visible where staff look for it, not only on the subscription row.
      if (variables?.donorId) {
        const amountText =
          variables.amount !== null && variables.amount !== undefined
            ? ` (${formatMoney(variables.amount)}/month)`
            : '';
        const description = [
          `Recurring donation cancelled${amountText}.`,
          variables.reason ? `Reason: ${variables.reason}.` : null,
          variables.notes ? `Notes: ${variables.notes}` : null
        ]
          .filter(Boolean)
          .join(' ');
        await createDonorFeedback({
          donar_id: variables.donorId,
          description,
          priority: 'Normal',
          user_id: user?.id || null,
          branch_id: variables.branchId || branchId || null
        });
        queryClient.invalidateQueries({ queryKey: ['donorFeedback'] });
      }
    },
    onError: error => {
      console.error('Cancel subscription error:', error);
      toast.error('Failed to cancel subscription');
    }
  });

  return {
    cancelSubscription: mutation.mutate,
    cancelSubscriptionAsync: mutation.mutateAsync,
    isCancelling: mutation.isPending
  };
}

// Hook to create a refund
export function useCreateRefund() {
  const { branchId } = useBranchPermission();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ orderId, amount, reason, notes }) => {
      return callEdgeFunction('create-refund', {
        orderId,
        branchId,
        amount,
        reason,
        notes
      });
    },
    onSuccess: () => {
      toast.success('Refund processed successfully');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
      queryClient.invalidateQueries({ queryKey: ['donorOrders'] });
      // A deleted donation may have been a pledge payment
      queryClient.invalidateQueries({ queryKey: ['pledge'] });
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
    },
    onError: error => {
      console.error('Create refund error:', error);
      toast.error(error.message || 'Failed to process refund');
    }
  });

  return {
    createRefund: mutation.mutate,
    createRefundAsync: mutation.mutateAsync,
    isRefunding: mutation.isPending
  };
}

// Delete a manual/offline order (cash, cheque, bank transfer, aMember). Card
// payments are refunded instead. Mirrors useCreateRefund but hits delete-order.
export function useDeleteOrder() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ orderId }) => {
      return callEdgeFunction('delete-order', { orderId });
    },
    onSuccess: () => {
      toast.success('Order deleted');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
      queryClient.invalidateQueries({ queryKey: ['donorOrders'] });
    },
    onError: error => {
      console.error('Delete order error:', error);
      toast.error(error.message || 'Failed to delete order');
    }
  });

  return {
    deleteOrder: mutation.mutate,
    deleteOrderAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Combined hook for payment methods management
export function useStripePayments() {
  const { branchId } = useBranchPermission();
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] =
    React.useState(false);
  const [isUpdatingDefaultMethod, setIsUpdatingDefaultMethod] =
    React.useState(false);

  const getPaymentMethods = React.useCallback(
    async ({ donorId }) => {
      setIsLoadingPaymentMethods(true);
      try {
        const result = await callEdgeFunction('manage-payment-methods', {
          action: 'list',
          donorId,
          branch_id: branchId
        });
        return result;
      } catch (error) {
        console.error('Get payment methods error:', error);
        return null;
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    },
    [branchId]
  );

  const makePaymentMethodDefault = async ({ donorId, paymentMethodId }) => {
    setIsUpdatingDefaultMethod(true);
    try {
      const result = await callEdgeFunction('manage-payment-methods', {
        action: 'setDefault',
        donorId,
        paymentMethodId,
        branch_id: branchId
      });
      return result;
    } catch (error) {
      console.error('Make payment method default error:', error);
      throw error;
    } finally {
      setIsUpdatingDefaultMethod(false);
    }
  };

  return {
    getPaymentMethods,
    makePaymentMethodDefault,
    isLoadingPaymentMethods,
    isUpdatingDefaultMethod
  };
}

// Pre-made donor emails (#20) - sent server-side with the Help Yateem letterhead
export function useSendDonorEmail() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ donorId, template, amount, billing }) =>
      callEdgeFunction('send-donor-email', {
        donorId,
        template,
        amount,
        billing
      }),
    onSuccess: result => {
      toast.success(`Email sent to ${result?.to || 'the donor'}`);
      queryClient.invalidateQueries({ queryKey: ['donorFeedback'] });
    },
    onError: error => {
      console.error('Send donor email error:', error);
      toast.error(error?.message || 'Failed to send email');
    }
  });
  return {
    sendDonorEmail: mutation.mutate,
    sendDonorEmailAsync: mutation.mutateAsync,
    isSending: mutation.isPending
  };
}
